import React, { Fragment } from "react";
import PropTypes from "prop-types";
import {
  Row,
  Col,
  Form,
  Input,
  InputNumber,
  Select,
  Upload,
  Button,
  Icon,
  Divider,
  Checkbox,
  Tooltip,
  message
} from "antd";
import { connect } from "react-redux";
import { Storage } from "aws-amplify";
import { makeBucketName, deletePublicImage } from "utils/bucket";
import { tosValidator, validImage } from "utils/validators";
import { labActions } from "redux/modules/lab";
import { mappingActions } from "redux/modules/mappings";
import { ulid } from "ulid";
import get from "lodash/get";
import CustomizeMapping from "components/custom-mapping";
import { getTirePrice } from 'utils/price';
import * as Styled from "./styled";

const FormItem = Form.Item;
const Option = Select.Option;

class OrderMapping extends React.Component {
  static propTypes = {
    form: PropTypes.object,
    catalogs: PropTypes.array,
    labData: PropTypes.object,
    order: PropTypes.object,
    onSave: PropTypes.func,
    onClose: PropTypes.func
  };

  state = {
    saving: false,
    catalogItems: {
      products: []
    },
    customMappings: {}
  };

  componentWillMount() {
    const mappings = [];
    this.props.order.transactions.forEach(tnx => {
      this.ids[tnx.product_id] = ulid();
      if (tnx.mapping && !tnx.mapping.ignore) {
        mappings.push(tnx.mapping);
      }
    });
    this.props.dispatch(mappingActions.searchMappingsSuccess(mappings));
  }

  ids = {};

  onSearch = query => {
    if (query.length >= 2)
      this.props.dispatch(mappingActions.searchMappings(query));
  };

  normFile = e => {
    if (Array.isArray(e)) {
      return e;
    }
    return e && e.fileList;
  };

  onCatalogChange = async (val, idx) => {
    this.props.form.setFieldsValue({
      [`lineItems[${idx}].product_pos_code`]: undefined
    });
  };

  getInitialName = (listing, product) => {
    if (!listing || !product) return "";
    let name = listing.title;
    const options = product.variations.map(i => i.formatted_name).join("-");
    if (options) {
      name = name + "-" + options;
    }
    return name;
  };

  onSubmit = e => {
    e.preventDefault();
    const { order, catalogs } = this.props;
    this.props.form.validateFields((err, values) => {
      if (!err) {
        let error = null;
        const items = [];
        values.lineItems.forEach((item, i) => {
          const tnx = order.transactions[i];
          item.productId = `etsy:${tnx.listing_id}:${
            tnx.product_id
          }`;
          item.title = tnx.title;
          // TODO etsy v3: Missing options
          // item.productOptions = tnx.variations.map(p => ({
          //   name: p.formatted_name,
          //   values: p.formatted_value
          // }));
          if (item.mappingId === "new") {
            // new mapping. Create payload for new mapping and productMapping
            const catalog = catalogs.find(
              cat => cat.cat_id === item.catalogId
            );
            const products = catalogs.find(c => c.cat_id === item.catalogId).products;
            const sosProduct = products.find(
              p => p.pos_code === item.product_pos_code
            );

            items.push({
              id: this.ids[tnx.product_id],
              name: item.name,
              images: item.images.map(i => i.response.key),
              catalogId: item.catalogId,
              catalogName: catalog.cat_name,
              product_pos_code: item.product_pos_code,
              product_size: sosProduct.size,
              quantity: item.quantity,
              productId: item.productId,
              title: item.title,
              productOptions: item.productOptions,
              saveMapping: item.saveMapping,
              customized: !item.saveMapping
            });
          } else if (item.customized) {
            const customMapping = this.state.customMappings[tnx.transaction_id];
            if (!customMapping) {
              error = true;
              return message.error(
                `Please customize order item: ${tnx.title} `
              );
            }
            // Customized mapping, send mapping object only.
            items.push({
              productId: item.productId,
              images: customMapping.images,
              catalogId: customMapping.catalogId,
              catalogName: customMapping.catalogName,
              product_pos_code: customMapping.product_pos_code,
              product_size: customMapping.product_size,
              quantity: customMapping.quantity
            });
          } else {
            // attached existing mapping to product
            items.push({
              productId: item.productId,
              mappingId: item.mappingId,
              productOptions: item.productOptions,
              title: item.title,
              saveMapping: item.saveMapping
            });
          }
        });

        if (error) {
          return;
        }

        this.setState({ saving: true });
        this.props.onSave({ lineItems: items, shippingMethod: values.shippingMethod }).catch(() => {
          this.setState({ saving: false });
        });
      }
    });
  };

  handleUpload = (
    { action, data, file, filename, headers, onError, onProgress, onSuccess },
    tnx,
    isCustomized
  ) => {
    const bucket = makeBucketName(
      this.ids[tnx.product_id],
      file.name
    );

    const metadata = isCustomized ? { custom: "true" } : null;

    Storage.vault
      .put(bucket, file, {
        metadata,
        progressCallback: progress => {
          const pc = (progress.loaded / progress.total) * 100;
          onProgress({ percent: pc }, file);
        }
      })
      .then(result => {
        onSuccess(result, file);
      })
      .catch(err => {
        console.log(err);
        onError(err);
      });
  };

  onRemove = e => {
    if (e.response) {
      const key = e.response.key;
      Storage.vault.remove(key).then(
        () => {
          deletePublicImage(key);
          message.success("File removed");
        },
        e => console.log(e.message)
      );
    }
  };

  onCustomMapSave = (mapping, tnx) => {
    const { customMappings } = this.state;
    customMappings[tnx.transaction_id] = mapping;
    this.setState({ customMappings });
  };

  render() {
    const {
      form: { getFieldDecorator, getFieldValue, setFieldsValue },
      order,
      catalogs,
      labData,
      mappings,
      searching,
      store,
    } = this.props;
    const { catalogItems, saving } = this.state;

    if (!labData) return null;

    const fileTypes =
      catalogItems.allowed_file_types || labData.allowed_file_types;
    const extensions =
      fileTypes &&
      fileTypes
        .split(",")
        .map(i => `.${i}`)
        .join(",");

    const lineItems = getFieldValue("lineItems");
    const hasPendingUploads =
      lineItems &&
      lineItems.find(l => {
        if (l.mappingId && l.mappingId !== "new") {
          return false;
        }
        return l.images ? !!l.images.find(f => !f.response) : true;
      });

    const formItemLayout = {
      labelCol: {
        xs: { span: 24 },
        sm: { span: 8 }
      },
      wrapperCol: {
        xs: { span: 24 },
        sm: { span: 16 }
      }
    };

    return (
      <Styled.Container>
        <Form onSubmit={this.onSubmit} hideRequiredMark>
          {order.transactions.map((tnx, idx) => {
            const catalogId = getFieldValue(`lineItems[${idx}].catalogId`);
            const mappingId = getFieldValue(`lineItems[${idx}].mappingId`);
            const customized = getFieldValue(`lineItems[${idx}].customized`);

            let products = [];

            if (catalogId) {
              products = catalogs.find(c => c.cat_id === catalogId).products;
            }
            return (
              <div key={tnx.transaction_id}>
                <h3>{tnx.title}</h3>
                <Row>
                  <Col span={24}>
                    <FormItem style={{ marginBottom: 10 }}>
                      {getFieldDecorator(`lineItems[${idx}].mappingId`, {
                        initialValue: get(tnx, "mapping.id"),
                        rules: [
                          { required: true, message: "Please select mapping" }
                        ]
                      })(
                        <Select
                          showSearch
                          size="large"
                          placeholder="Search for an existing mapping or create a new one"
                          onSearch={this.onSearch}
                          onSelect={val => {
                            const map = mappings.find(m => m.id === val);
                            setFieldsValue({
                              [`lineItems[${idx}].customized`]:
                                map && map.customized
                            });
                          }}
                          filterOption={false}
                          loading={searching}
                        >
                          <Select.Option value="new">
                            <strong>Create a new mapping</strong>
                          </Select.Option>
                          {mappings.map(m => (
                            <Select.Option value={m.id}>
                              {m.name} - SKU:{m.sku}
                            </Select.Option>
                          ))}
                        </Select>
                      )}
                    </FormItem>
                  </Col>
                </Row>
                {getFieldDecorator(`lineItems[${idx}].customized`, {
                  initialValue: tnx.mapping && tnx.mapping.customized
                })(<Input type="hidden" />)}
                {customized && (
                  <CustomizeMapping
                    transaction={tnx}
                    mapping={tnx.mapping}
                    mappingId={mappingId}
                    onChange={this.onCustomMapSave}
                  />
                )}
                {mappingId === "new" && (
                  <Fragment>
                    <FormItem label="Mapping name">
                      {getFieldDecorator(`lineItems[${idx}].name`, {
                        initialValue: this.getInitialName(
                          tnx,
                          tnx
                        ),
                        rules: [
                          {
                            required: true,
                            message: "Please name this mapping"
                          }
                        ]
                      })(
                        <Input placeholder="e.g., Cutest Prints Ever, Set of 4 8x10s" />
                      )}
                    </FormItem>
                    <Row gutter={12}>
                      <Col lg={8} sm={8}>
                        <FormItem>
                          {getFieldDecorator(`lineItems[${idx}].catalogId`, {
                            rules: [
                              {
                                required: true,
                                message: "Please select catalog"
                              }
                            ]
                          })(
                            <Select
                              onChange={val => this.onCatalogChange(val, idx)}
                              placeholder="Please select a catalog"
                            >
                              {catalogs.map(c => (
                                <Select.Option value={c.cat_id} key={c.cat_id}>
                                  {c.cat_name}
                                </Select.Option>
                              ))}
                            </Select>
                          )}
                        </FormItem>
                      </Col>
                      <Col lg={8} sm={8}>
                        <FormItem>
                          {getFieldDecorator(
                            `lineItems[${idx}].product_pos_code`,
                            {
                              rules: [
                                {
                                  required: true,
                                  message: "Please select a product"
                                }
                              ]
                            }
                          )(
                            <Select placeholder="Please select a product">
                              {products.map(p => (
                                <Select.Option
                                  value={p.pos_code}
                                  key={p.pos_code}
                                >
                                  {p.size} (${getTirePrice(p.price)} ea)
                                </Select.Option>
                              ))}
                            </Select>
                          )}
                        </FormItem>
                      </Col>
                      <Col lg={8} sm={8}>
                        <FormItem label="Quantity" {...formItemLayout}>
                          {getFieldDecorator(`lineItems[${idx}].quantity`, {
                            rules: [
                              { required: true, message: "Enter quantity" }
                            ],
                            initialValue: 1
                          })(<InputNumber min={1} max={1000} />)}
                        </FormItem>
                      </Col>
                    </Row>
                    <Form.Item label="Upload Images - Must be RGB, JPG's only">
                      {getFieldDecorator(`lineItems[${idx}].images`, {
                        valuePropName: "fileList",
                        getValueFromEvent: this.normFile,
                        rules: [
                          { required: true, message: "Please upload files" }
                        ]
                      })(
                        <Upload
                          multiple
                          accept={extensions}
                          listType="picture"
                          customRequest={r =>
                            this.handleUpload(r, tnx, customized)
                          }
                          beforeUpload={validImage}
                          onRemove={this.onRemove}
                        >
                          <Button>
                            <Icon type="upload" /> Click to select
                          </Button>
                        </Upload>
                      )}
                    </Form.Item>
                  </Fragment>
                )}
                {!(customized || tnx.mapping) && (
                  <FormItem
                    help="Do not save customized products. Customized products must be ordered manually"
                    style={{ marginBottom: 20 }}
                  >
                    {getFieldDecorator(`lineItems[${idx}].saveMapping`, {
                      valuePropName: "checked"
                    })(<Checkbox>Save mappings for future orders</Checkbox>)}
                  </FormItem>
                )}
                {mappingId === "new" && (
                  <FormItem>
                    {getFieldDecorator("tos", {
                      valuePropName: "checked",
                      rules: [{ validator: tosValidator }]
                    })(
                      <Checkbox className="multiline">
                      I represent and warrant that I possess all necessary rights
                      with respect to these images, and that they do not infringe
                      or otherwise violate any copyright, trade secret right or
                      other intellectual property or other property or proprietary
                      right of any third party. I  agree to indemnify and hold Fromex Photo Lab, Inc and Qprints.com
                       and their directors, officers and employees harmless from and against any and all claims, liabilities, causes of action,
                       damages, losses and expenses (including attorneys’ fees) that may arise from printing these images.
                      </Checkbox>
                    )}
                  </FormItem>
                )}
                <Divider />
              </div>
            );
          })}
          <FormItem label="Please select default shipping method">
            {getFieldDecorator(`shippingMethod`, {
              initialValue: !order.forceCustomization ? store.defaultShippingOption : undefined,
              rules: [{ required: true, message: 'Please select shipping method' }]
            })(
              <Select placeholder="Please select">
                {labData.shipping.filter(i => i.shiptype ==='dropship').map((s) => (
                  <Option key={s.pos_code} value={s.pos_code}>
                    {s.shippingmethod} ${s.price}
                  </Option>
                ))}
              </Select>
            )}
          </FormItem>
          <div className="text-right">
            <Button size="large" onClick={this.props.onClose}>
              Cancel
            </Button>{" "}
            <Button
              disabled={hasPendingUploads}
              loading={saving}
              type="primary"
              size="large"
              icon="save"
              htmlType="submit"
            >
              Submit order
            </Button>
          </div>
        </Form>
      </Styled.Container>
    );
  }
}

export default connect((state) => ({
  ...state.lab,
  mappings: state.mappings.searchResults,
  searching: state.mappings.searching,
  store: state.session.stores.find(
    s => s.storeId === state.session.currentStoreId
  )
}))(Form.create()(OrderMapping));
